import MousePosition from "ol/control/MousePosition";
import GIS from "../app"
import {CoordinatesPanelOptions} from "../configuration/control";
import {Coordinate} from "ol/coordinate";
import {ProjectionLike} from "ol/proj";
import {toLonLat, transform} from "../map";
import {Size} from "ol/size";
import {Interaction as olInteraction} from "ol/interaction";
import {InteractionExtension} from "../configuration/map.ts";

class CoordinatesPanelControl extends MousePosition {
    private readonly GIS;
    private readonly defaultProjection;
    private readonly className;
    private readonly localUserProjection;
    private readonly coordinatesContainer;
    private readonly coordinatesPanel;
    private readonly editToTrashButtonContainer;
    private readonly pointIconContainer;
    private readonly removeOverlayButtonContainer;
    private readonly crsButton;
    private readonly crsButtonContainer;
    private identify: olInteraction & InteractionExtension;
    private editIconContainer;
    private readonly options: CoordinatesPanelOptions;

    constructor(options: CoordinatesPanelOptions, GIS: GIS) {
        super({
            target: options.target,
            coordinateFormat: (coordinates) => {
                if (coordinates) {
                    this.coordinatesProcessing(this.GIS.View.olObject.getProjection().getCode(), [coordinates[0], coordinates[1]]);
                }

                //Unused but enforced by typescript
                return '';
            }
        });

        this.options = options;
        this.className = options.className ? options.className : ".ol-mouse-position";
        this.defaultProjection = 'EPSG:4326';
        this.localUserProjection = options.projection;

        this.GIS = GIS;

        this.coordinatesPanel = document.createElement("div");
        this.coordinatesContainer = document.createElement('div');
        this.crsButton = document.createElement('button');
        this.removeOverlayButtonContainer = document.createElement('div');
        this.pointIconContainer = document.createElement('div');
        this.editToTrashButtonContainer = document.createElement('div');
        this.editIconContainer = document.createElement('div');
        this.crsButtonContainer = document.createElement('div');
        this.identify = this.GIS.Interaction.getInteraction('Identify') as olInteraction & InteractionExtension;
        this.createCoordinatesPanel(options);
    }

    public getClassName = () => {
        return this.className;
    }

    private coordinatesCheck(coordinate: Coordinate, projection: ProjectionLike) {
        try {
            toLonLat(coordinate, projection, null);
        } catch (e) {
            this.coordinatesContainer.title = this.GIS.Localisation.resolve(
                this.options?.i18n?.coordinatesError,
                'Coordinates: Check coordinates format "x, y"'
            );

            if (!this.coordinatesContainer.classList.contains('error')) {
                this.coordinatesContainer.classList.toggle("error");
            }

            if (e instanceof Error) {
                window.console && console.warn(e.message);
            }
        }
    }

    private cancelErrorStatus() {
        if (this.coordinatesContainer && this.coordinatesContainer.classList.contains('error')) {
            this.coordinatesContainer.classList.toggle("error");
        }
    }

    private cancelEditing() {
        if (this.coordinatesContainer && this.coordinatesContainer.classList.contains('edit')) {
            this.coordinatesContainer.classList.toggle("edit");
            this.editIconContainer.style.display = "none";
            this.editToTrashButtonContainer.style.display = "none";
            this.coordinatesContainer.blur();
            this.cancelErrorStatus();
        }
    }

    private copyToClipboard() {
        let copyText = this.coordinatesContainer;
        navigator.clipboard.writeText(copyText.textContent as string);
    }

    private cancelPointFixation() {
        this.identify.removePointer();
        this.removeOverlayButtonContainer.style.display = "none";
        this.pointIconContainer.style.display = "none";
        this.cancelEditing();
        this.cancelErrorStatus();
    }

    private changeCoordinatesCRS(projection: ProjectionLike) {
        this.crsButton.classList.toggle("wgs");
        let coordinatesStrings = this.coordinatesContainer.textContent?.split(" ");
        let coordinatesNumbers = coordinatesStrings?.map(str => {
            return parseFloat(str);
        });
        if (coordinatesNumbers && coordinatesNumbers.length === 2) {
            coordinatesNumbers = [coordinatesNumbers[1], coordinatesNumbers[0]];

            let sourceEpsg;
            let destinationEpsg;
            let numberOfDigits;
            if (this.crsButton.classList.contains('wgs')) {
                destinationEpsg = this.defaultProjection;
                sourceEpsg = projection;
                this.crsButton.innerHTML = '<i class="fal fa-globe"/>';
                numberOfDigits = 100000;
            } else {
                sourceEpsg = this.defaultProjection;
                destinationEpsg = projection;
                this.crsButton.innerHTML = '<i class="fal fa-map"/>';
                numberOfDigits = 100;
            }

            let coordinatesToWrite = transform(coordinatesNumbers, sourceEpsg, destinationEpsg, null);

            this.coordinatesContainer.innerHTML = Math.round(coordinatesToWrite[1] * numberOfDigits) / numberOfDigits + ', ' + Math.round(coordinatesToWrite[0] * numberOfDigits) / numberOfDigits;
        } else {
            console.warn('Failed to change CRS in coordinates panel!')
        }
    }

    private moveToCoordinates(projection: ProjectionLike, crsButton: HTMLButtonElement, minZoom: number) {

        let coordinatesStrings = this.coordinatesContainer.textContent?.split(" ");
        let coordinatesNumbers = coordinatesStrings?.map(str => {
            return parseFloat(str);
        });
        if (coordinatesNumbers && coordinatesNumbers.length === 2) {
            coordinatesNumbers = [coordinatesNumbers[1], coordinatesNumbers[0]];
            let sourceEpsg;
            if (crsButton.classList.contains('wgs')) {
                sourceEpsg = this.defaultProjection;
            } else {
                sourceEpsg = projection;
            }

            this.coordinatesCheck([coordinatesNumbers[0], coordinatesNumbers[1]], sourceEpsg);

            const dividedNum = (num: number) => Math.round(num / 2);

            const size = this.GIS.Map.olObject!.getSize() as Size;
            this.GIS.View.olObject.centerOn(transform(coordinatesNumbers, sourceEpsg, this.GIS.View.olObject.getProjection().getCode(), null), size, size.map(dividedNum));
            if (this.identify.hasIconOverlay()) {
                this.identify.markerToCoordinates(transform(coordinatesNumbers, sourceEpsg, this.GIS.View.olObject.getProjection().getCode(), null));
                this.removeOverlayButtonContainer.style.display = "inline-block";
                this.pointIconContainer.style.display = "inline-block";
            }

            const zoom = this.GIS.View.getZoom(null);
            if (zoom && zoom < minZoom) {
                this.GIS.View.setZoom(minZoom)
            }

            this.cancelErrorStatus();
        }
    }

    private createCoordinatesPanel(options: CoordinatesPanelOptions) {
        this.coordinatesPanel.className = "cfm-coordinates-panel";

        this.crsButtonContainer.className = 'coordinates-button-container';

        this.crsButton.className = 'coordinates-button';
        this.crsButton.innerHTML = '<i class="fal fa-map"/>';
        this.crsButton.type = 'button';
        this.GIS.Localisation.bindTitle(
            this.crsButton,
            options.i18n?.changeCRS,
            'Change CRS'
        );
        this.crsButtonContainer.appendChild(this.crsButton);
        this.coordinatesPanel.appendChild(this.crsButtonContainer);

        this.pointIconContainer.className = 'point-icon-container';
        const pointIcon = document.createElement('div');
        pointIcon.className = 'point-icon';
        pointIcon.innerHTML = '<i class="fal fa-map-marker-question"/>';
        this.pointIconContainer.appendChild(pointIcon);
        this.coordinatesPanel.appendChild(this.pointIconContainer);

        this.editIconContainer.className = 'edit-icon-container';
        const editIcon = document.createElement('div');
        editIcon.className = 'edit-icon';
        editIcon.innerHTML = '<i class="fal fa-pen-to-square"/>';
        this.editIconContainer.appendChild(editIcon);

        this.coordinatesContainer.className = 'cfm-coordinates-container';
        this.coordinatesContainer.setAttribute("contenteditable", 'true');
        this.GIS.Localisation.bindTitle(
            this.coordinatesContainer,
            options.i18n?.coordinates,
            'Coordinates'
        );
        this.coordinatesPanel.appendChild(this.coordinatesContainer);

        this.editToTrashButtonContainer.className = 'edit-to-trash-button-container';
        const editToTrashButton = document.createElement('button');
        editToTrashButton.className = ('edit-to-trash-button');
        editToTrashButton.innerHTML = '<i class="fal fa-times"/>';
        this.GIS.Localisation.bindTitle(
            editToTrashButton,
            options.i18n?.stopEditing,
            'Stop editing'
        );
        this.editToTrashButtonContainer.appendChild(editToTrashButton);
        this.coordinatesPanel.appendChild(this.editToTrashButtonContainer);

        this.removeOverlayButtonContainer.className = 'remove-overlay-button-container';
        const removeOverlayButton = document.createElement('button');
        removeOverlayButton.className = 'remove-overlay-button';
        removeOverlayButton.innerHTML = '<i class="fal fa-trash-can"/>';
        this.GIS.Localisation.bindTitle(
            removeOverlayButton,
            options.i18n?.removePoint,
            'Remove point from map'
        );
        this.removeOverlayButtonContainer.appendChild(removeOverlayButton);
        this.coordinatesPanel.appendChild(this.removeOverlayButtonContainer);

        let element = document.createElement('div');
        element.className = 'copy-to-clipboard-button-container';
        const copyToClipboardButton = document.createElement('button');
        copyToClipboardButton.className = 'copy-to-clipboard-button';
        copyToClipboardButton.innerHTML = '<i class="fal fa-copy"/>';
        copyToClipboardButton.type = 'button';
        this.GIS.Localisation.bindTitle(
            copyToClipboardButton,
            options.i18n?.copyToClipboard,
            'Copy to clipboard'
        );
        element.appendChild(copyToClipboardButton);
        this.coordinatesPanel.appendChild(element);

        element = document.createElement('div');
        element.className = 'move-to-coordinates-button-container';
        const moveToCoordinatesButton = document.createElement('button');
        moveToCoordinatesButton.className = 'move-to-coordinates-button';
        moveToCoordinatesButton.innerHTML = '<i class="fal fa-map-location"/>';
        this.GIS.Localisation.bindTitle(
            moveToCoordinatesButton,
            options.i18n?.moveToCoordinates,
            'Move to coordinates'
        );
        moveToCoordinatesButton.type = 'button';

        element.appendChild(moveToCoordinatesButton);
        this.coordinatesPanel.appendChild(element);

        const targetElement = this.GIS.Map.olObject!.getTargetElement();
        targetElement.appendChild(this.coordinatesPanel);

        removeOverlayButton.addEventListener('click', () => {
            this.cancelPointFixation()
        });
        this.crsButton.addEventListener('click', () => {
            this.changeCoordinatesCRS(options.projection)
        });
        copyToClipboardButton.addEventListener('click', () => {
            this.copyToClipboard();
        });
        moveToCoordinatesButton.addEventListener('click', () => {
            this.moveToCoordinates(options.projection, this.crsButton, options.minZoom);
        })
        this.coordinatesContainer.addEventListener('keypress', event => {
            if (event.key === "Enter") {
                event.preventDefault();
                this.moveToCoordinates(options.projection, this.crsButton, options.minZoom);
            }
        });

        this.coordinatesContainer.addEventListener('focus', () => {
            if (!this.coordinatesContainer.classList.contains('edit')) {
                this.coordinatesContainer.classList.toggle("edit");
                this.editIconContainer.style.display = "inline-block";
                this.editToTrashButtonContainer.style.display = "inline-block";
            }
        });

        editToTrashButton.addEventListener('click', () => {
            this.cancelEditing();
        });
        removeOverlayButton.addEventListener('click', () => {
            this.cancelPointFixation()
        });
    }

    private coordinatesProcessing(projection: ProjectionLike, coordinates: Coordinate) {
        let x = coordinates[0];
        let y = coordinates[1];
        let destinationEpsgText = this.localUserProjection;
        const featureArray = this.identify.getLayer()?.getSource().getFeatures();
        let numberOfDigits;
        let coordinatesToWrite;

        if (this.coordinatesContainer && this.crsButton.classList.contains('wgs')) {
            destinationEpsgText = this.defaultProjection;

            coordinatesToWrite = transform([x, y], projection, destinationEpsgText, null);
            numberOfDigits = 100000;
        } else {
            if (projection === this.localUserProjection) {

                coordinatesToWrite = [x, y];
                numberOfDigits = 100;
            } else {
                coordinatesToWrite = transform([x, y], projection, destinationEpsgText, null);
                numberOfDigits = 100;
            }
        }

        if (this.identify.getLayer()?.getSource().getFeatures().length > 0) {
            if (this.identify.hasIconOverlay()) {
                this.removeOverlayButtonContainer.style.display = "inline-block";
                this.pointIconContainer.style.display = "inline-block";
            } else {
                this.removeOverlayButtonContainer.style.display = "inline-block";
            }
        }

        if ((this.identify.getLayer()?.getSource().getFeatures().length > 0) || (this.coordinatesContainer.classList.contains('edit'))) {
            for (let order in featureArray) {
                let featureItem = featureArray[order];
                const featureName = featureItem.get('name')

                if (featureName === 'identify_point') {
                    coordinatesToWrite = transform(featureItem.getGeometry().getCoordinates(), this.GIS.View.olObject.getProjection().getCode(), destinationEpsgText, null);
                    if (this.identify.hasIconOverlay()) {
                        this.removeOverlayButtonContainer.style.display = "inline-block";
                        this.pointIconContainer.style.display = "inline-block";
                    }
                }
            }
        }

        if (!(this.coordinatesContainer.classList.contains('edit'))) {
            let sortedCoordinatesToWrite = coordinatesToWrite;
            if (this.coordinatesContainer && this.crsButton.classList.contains('wgs')) {
                sortedCoordinatesToWrite = [coordinatesToWrite[1], coordinatesToWrite[0]];
            }
            this.coordinatesContainer.innerHTML = Math.round(sortedCoordinatesToWrite[0] * numberOfDigits) / numberOfDigits + ', ' + Math.round(sortedCoordinatesToWrite[1] * numberOfDigits) / numberOfDigits;
        }
    }
}

export default CoordinatesPanelControl;

